import { SxProps, Theme } from '@mui/material';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import { ComponentType, useCallback, useState } from 'react';

import { Invoice } from '~hooks/useInvoices/models';

import ActionsColumn from './Columns/ActionsColumn';
import CheckboxColumn from './Columns/CheckboxColumn';
import DriverAndVendorColumn from './Columns/DriverAndVendorColumn';
import DriverStartColumn from './Columns/DriverStartColumn';
import DriverStopColumn from './Columns/DriverStopColumn';
import LoadsCompletedColumn from './Columns/LoadsCompletedColumn';
import TicketQuantityColumn from './Columns/TicketQuantityColumn';
import TotalColumn from './Columns/TotalColumn';
import TotalsHoursColumn from './Columns/TotalsHoursColumn';
import TruckColumn from './Columns/TruckColumn';
import TruckTypeColumn from './Columns/TruckTypeColumn';
import ExpandedBlock from './ExpandedBlock/ExpandedBlock';
import {
  columns,
  gridTemplateColumns,
  InvoicesTableColumnKey,
} from './invoices-table-columns';

interface ColumnComponentProps {
  invoice: Invoice;
  sx?: SxProps<Theme>;
}

// keyed so a column with no matching entry here is a compile error, not a missing cell
const columnComponentsByKey: Record<
  InvoicesTableColumnKey,
  ComponentType<ColumnComponentProps>
> = {
  checkbox: CheckboxColumn,
  driver_and_vendor: DriverAndVendorColumn,
  truck: TruckColumn,
  truck_type: TruckTypeColumn,
  driver_start: DriverStartColumn,
  driver_stop: DriverStopColumn,
  totals_hours: TotalsHoursColumn,
  loads_completed: LoadsCompletedColumn,
  ticket_quantity: TicketQuantityColumn,
  total: TotalColumn,
  actions: ActionsColumn,
};

const rowGridColumn = `span ${columns.length}`;

interface TableRowProps {
  invoice: Invoice;
}

export default function TableRow({ invoice }: TableRowProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  const toggleExpanded = useCallback(() => {
    setIsExpanded((expanded) => !expanded);
  }, []);

  return (
    <>
      <Box
        data-ui={`page-approvals__driver-pay__invoices-table__row-${invoice.id}`}
        onClick={toggleExpanded}
        sx={(theme) => ({
          borderBottom: `solid 1px ${theme.brandV2.colors.treadGray7}`,
          display: 'grid',
          gridAutoRows: '95px',
          gridColumn: rowGridColumn,
          gridTemplateColumns,
          overflow: 'hidden',
        })}
      >
        {columns.map((column) => {
          const ColumnComponent = columnComponentsByKey[column.key];

          return <ColumnComponent key={column.key} invoice={invoice} sx={column.sx} />;
        })}
      </Box>

      <Collapse
        in={isExpanded}
        mountOnEnter
        unmountOnExit
        sx={{ gridColumn: rowGridColumn }}
      >
        <Box
          sx={(theme) => ({
            borderBottom: `solid 1px ${theme.brandV2.colors.treadGray7}`,
          })}
        >
          <ExpandedBlock invoice={invoice} />
        </Box>
      </Collapse>
    </>
  );
}
